import React, { type ReactNode } from 'react';
import { useFragment } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import {
	ViewPageConversationFocusedInlineCommentFragment,
	ViewPageConversationFocusedInlineCommentContextFragment,
} from '@confluence/inline-comments-queries/entry-points/ViewPageConversation.ts';
import type {
	ViewPageConversation_focusedInlineComment$data,
	ViewPageConversation_focusedInlineComment$key,
} from '@confluence/inline-comments-queries/entry-points/ViewPageConversationFocusedInlineCommentTypes.ts';
import type {
	ViewPageConversation_focusedInlineCommentContext$data,
	ViewPageConversation_focusedInlineCommentContext$key,
} from '@confluence/inline-comments-queries/entry-points/ViewPageConversationFocusedInlineCommentContextTypes.ts';

import query, {
	type ObjectCommentContainerPreloadObjectCommentsQuery$data,
	type ObjectCommentContainerPreloadObjectCommentsQuery$variables,
} from './components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import { buildPreloadObjectCommentsQueryVariables } from './preloadObjectCommentsVariables.ts';

type FocusedCommentData = {
	comments: ViewPageConversation_focusedInlineComment$data | null;
	content: ViewPageConversation_focusedInlineCommentContext$data['focusedContent'];
	user: ViewPageConversation_focusedInlineCommentContext$data['user'];
};

export type ConversationFocusedInlineCommentResult = {
	data: FocusedCommentData | undefined;
	error?: ApolloErrorInterop;
};

type Props = {
	contentId: string;
	focusedCommentId?: string;
	skip?: boolean;
	children: (result: ConversationFocusedInlineCommentResult | undefined) => ReactNode;
};

const ConversationFocusedInlineCommentReader = ({
	contentId,
	focusedCommentId,
	children,
}: Props) => {
	const { data, error } = useQueryInterop<
		ObjectCommentContainerPreloadObjectCommentsQuery$data,
		ObjectCommentContainerPreloadObjectCommentsQuery$variables
	>(query, {
		variables: buildPreloadObjectCommentsQueryVariables(
			contentId,
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_data_frag_reader') &&
				expVal('confluence_exp_conversation_contract', 'isEnabled', false),
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
				expVal('confluence_exp_conversation_contract', 'isEnabled', false),
			focusedCommentId,
			true,
		),
		fetchPolicy: 'cache-first',
	});
	const comments = useFragment<ViewPageConversation_focusedInlineComment$key>(
		ViewPageConversationFocusedInlineCommentFragment,
		data?.focusedInlineComment,
	);
	const context = useFragment<ViewPageConversation_focusedInlineCommentContext$key>(
		ViewPageConversationFocusedInlineCommentContextFragment,
		data?.focusedInlineCommentContext,
	);

	return children({
		data: context
			? { comments: comments ?? null, content: context.focusedContent, user: context.user }
			: undefined,
		error,
	});
};

// Undefined means the legacy reader still owns this surface; an empty result means
// the contract owns it but is loading or returned no accessible focused comment.
export const ConversationFocusedInlineCommentData = (props: Props): ReactNode => {
	if (
		!props.skip &&
		props.contentId &&
		props.focusedCommentId &&
		isRelayEnabled('@confluence/object-comments', {
			operationName: 'ObjectCommentContainerPreloadObjectCommentsQuery',
		}) &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_query') &&
		expVal('confluence_exp_conversation_contract', 'isEnabled', false)
	) {
		return (
			<SuspenseWithDataFetcher dataFetcherName="object-comments">
				<ConversationFocusedInlineCommentReader
					key={`${props.contentId}:${props.focusedCommentId}`}
					{...props}
				/>
			</SuspenseWithDataFetcher>
		);
	}
	return props.children(undefined);
};
